import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql } from '@apollo/client/react/hoc';
import { compose } from 'recompose';
import isEmpty from 'lodash/isEmpty';
import head from 'lodash/head';
import { LoaderKeetUI } from '@keethealth/ui-components';
import cN from 'classnames';

import queries from 'graph/queries';
import CURRENT_USER from 'graph/queries/currentUser';
import Panel from 'common/Panel';
import { getMessagingOtherParty } from 'utils/chat';
import Window from './Window';
import NewChat from './NewChat';

import '../messages.scss';

const DESKTOP_LAYOUT_QUERY = '(min-width: 48em)';

function useDesktopLayout() {
  const [isDesktopLayout, setIsDesktopLayout] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.matchMedia(DESKTOP_LAYOUT_QUERY).matches;
  });

  useEffect(() => {
    const media = window.matchMedia(DESKTOP_LAYOUT_QUERY);
    const handleChange = () => setIsDesktopLayout(media.matches);
    handleChange();

    if (media.addEventListener) {
      media.addEventListener('change', handleChange);
      return () => media.removeEventListener('change', handleChange);
    }

    media.addListener(handleChange);
    return () => media.removeListener(handleChange);
  }, []);

  return isDesktopLayout;
}

export function Messages({ conversations, loadingConversations, userId }) {
  const isDesktopLayout = useDesktopLayout();
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [isStartingChat, setIsStartingChat] = useState(false);
  const [mobileTab, setMobileTab] = useState('selector');

  useEffect(() => {
    if (!isEmpty(conversations) && isEmpty(selectedConversationId)) {
      setSelectedConversationId(head(conversations).id);
    }
  }, [conversations, selectedConversationId])

  return (
    <>
      <Panel id="scene-messages" className="flex flex-column">
        <div className="relative flex flex-column flex-auto">
          <div className="f4 bb pa3 tc messages-header-bar">
            {!isDesktopLayout && mobileTab === 'messages' && (
              <button
                type="button"
                className="messages-back-button fl primary bn pn pointer bg-inherit cursor"
                onClick={handleBack}
                aria-label="Back to conversations"
              >
                <i className="fas fa-2x fa-arrow-left" aria-hidden="true" />
              </button>
            )}
            <h1 className="f4 ma0">Messages</h1>
          </div>
          {isDesktopLayout ? (
            <div className="flex flex-auto message-height min-h-0">
              {!isStartingChat && renderNewConversationButton()}
              {renderMessageSelector('desktop')}
              {renderWindow()}
            </div>
          ) : (
            <div className="flex flex-column flex-auto message-height min-h-0">
              {!isStartingChat && renderNewConversationButton()}
              {mobileTab === 'selector' && renderMessageSelector('mobile')}
              {mobileTab === 'messages' && renderWindow()}
            </div>
          )}
        </div>
      </Panel>
    </>
  );
  function handleSelectConversation(conversationId) {
    return () => {
      setSelectedConversationId(conversationId);
      setIsStartingChat(false);
      setMobileTab('messages');
    }
  }

  function handleBack() {
    setMobileTab('selector');
    setIsStartingChat(false);
  }

  function handleNew() {
    setIsStartingChat(true);
    setMobileTab('messages');
  }

  function renderNewConversationButton() {
    return (
      <button
        type="button"
        className="messages-new-conversation primary bn pointer bg-inherit"
        onClick={handleNew}
        aria-label="Start new conversation"
      >
        <i className="fas fa-2x fa-plus-circle" aria-hidden="true" />
      </button>
    );
  }

  function renderWindow() {
    if (isStartingChat) {
      return <NewChat onSelectConversation={handleSelectConversation} />;
    }
    if (loadingConversations) {
      return <LoaderKeetUI color="primary" />;
    }
    if (selectedConversationId) {
      return (
        <Window
          conversationId={selectedConversationId}
        />
      );
    }
  }

  function renderMessageSelector(size) {
    return (
      <ul id="messages-selector" className={`br ma0 pa0 overflow-auto ${size === 'desktop' && 'message-selector-desktop'}`}>
        {conversations.map((conversation) => {
          const otherParty = getMessagingOtherParty(userId, conversation);
          const isSelected = conversation.id === selectedConversationId;
          return (
            <li key={conversation.id} className="ma0 pa0">
              <button
                type="button"
                onClick={handleSelectConversation(conversation.id)}
                className={cN(
                  'selector-item flex items-center link pa3 pointer w-100 bn tl bg-inherit',
                  { active: isSelected },
                )}
                aria-current={isSelected ? 'true' : undefined}
              >
                <div className="flex flex-column flex-auto overflow-hidden">
                  <div className="flex justify-between flex-column">
                    <span>
                      {conversation.topic}
                    </span>
                    <span className="flex-shrink-0 f6 silver">
                      {`${otherParty.firstName} ${otherParty.lastName}`}
                    </span>
                  </div>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    );
  }
}

Messages.propTypes = {
  conversations: PropTypes.array,
  loadingConversations: PropTypes.bool.isRequired,
  userId: PropTypes.string,
};

Messages.defaultProps = {
  conversations: [],
  userId: '',
};

export default compose(
  graphql(queries.conversations, {
    props: ({ data: { loading, conversations } }) => ({
      conversations,
      loadingConversations: loading,
    }),
  }),
  graphql(CURRENT_USER, {
    options: { fetchPolicy: 'cache-and-network' },
    props: ({ data: { currentUser } }) => ({
      userId: currentUser && currentUser.id,
    }),
  }),
)(Messages);
